"use client";

import { Rocket } from "lucide-react";
import { useActionState, useState } from "react";
import { inputClass } from "@/components/forms/Field";
import Link from "@/components/ui/Link";
import { SweetCoin } from "@/components/coins/SweetCoin";
import type { BoostText } from "@/lib/boost/i18n";
import { buyBoostAction, type BoostState } from "@/lib/boost/actions";
import type { BoostKind } from "@/lib/boost/plans";
import { fmt } from "@/lib/locales/fmt";

// Choosing and buying a boost (app/[lang]/(site)/boost): the kind, a category or hours where it takes them, who sees
// it (a country, a language), the total against the member's bought coins.

/** Countries a boost can be shown in: those Sweet's visitors come from most, then the rest of the larger ones. */
export const BOOST_COUNTRIES = [
  "us", "gb", "de", "fr", "es", "it", "nl", "pl", "pt", "br", "mx", "ar", "co", "cl", "pe", "ca", "au", "nz", "ie", "be", "ch", "at", "se", "no", "dk",
  "fi", "cz", "sk", "hu", "ro", "bg", "gr", "hr", "rs", "ua", "ru", "by", "kz", "uz", "tr", "il", "ae", "sa", "eg", "ma", "za", "ng", "ke", "in", "pk",
  "bd", "th", "vn", "ph", "id", "my", "sg", "jp", "kr", "tw", "hk",
];

/** A country's name in the page's language ("DE" → "Deutschland"), or its code where the browser has none. */
function regionNamer(lang: string): (code: string) => string {
  try {
    const names = new Intl.DisplayNames([lang], { type: "region" });
    return (c) => names.of(c.toUpperCase()) ?? c.toUpperCase();
  } catch {
    return (c) => c.toUpperCase();
  }
}

export function BoostOrder({
  t,
  lang,
  target,
  kinds,
  prices,
  maxHours,
  categories,
  languages,
  balance,
  queue,
}: {
  t: BoostText;
  lang: string;
  target: { kind: "profile"; slug: string } | { kind: "room"; platform: string; username: string };
  kinds: BoostKind[];
  prices: Record<BoostKind, number>;
  maxHours: number;
  categories: { slug: string; name: string }[];
  languages: { code: string; name: string }[];
  balance: number;
  /** When a kind with few places would start now, if not at once (ISO). */
  queue: Partial<Record<BoostKind, string>>;
}) {
  const [state, action, pending] = useActionState<BoostState, FormData>(buyBoostAction, {});
  const [kind, setKind] = useState<BoostKind>(kinds[0]);
  const [hours, setHours] = useState(1);
  const total = kind === "room_hours" ? prices.room_hours * hours : prices[kind];
  const regionName = regionNamer(lang);
  const countries = [...BOOST_COUNTRIES].sort((a, b) => regionName(a).localeCompare(regionName(b), lang));
  const when = (iso: string) => new Intl.DateTimeFormat(lang, { day: "numeric", month: "long", hour: "2-digit", minute: "2-digit" }).format(new Date(iso));

  return (
    <form action={action} className="space-y-6">
      <input type="hidden" name="ui" value={lang} />
      <input type="hidden" name="target" value={target.kind} />
      {target.kind === "profile" ? (
        <input type="hidden" name="slug" value={target.slug} />
      ) : (
        <>
          <input type="hidden" name="platform" value={target.platform} />
          <input type="hidden" name="username" value={target.username} />
        </>
      )}

      <fieldset>
        <legend className="mb-3 text-[17px] text-fg">{t.chooseKind}</legend>
        <div className="grid gap-2.5 sm:grid-cols-2">
          {kinds.map((k) => (
            <label
              key={k}
              className={`flex cursor-pointer flex-col gap-1 rounded-2xl border p-4 transition-colors ${k === kind ? "border-brand bg-brand/[0.08]" : "border-line bg-raised hover:border-brand/40"}`}
            >
              <span className="flex items-start justify-between gap-3">
                <span className="flex items-center gap-2 text-[15px] text-fg">
                  <input type="radio" name="kind" value={k} checked={k === kind} onChange={() => setKind(k)} className="accent-[var(--color-brand)]" />
                  {t.kinds[k]}
                </span>
                <span className="shrink-0 text-[13.5px] tabular-nums text-brand-soft">{k === "room_hours" ? fmt(t.perHour, { n: prices[k] }) : fmt(t.coins, { n: prices[k] })}</span>
              </span>
              <span className="pl-6 text-[12.5px] leading-snug text-muted">{t.kindHints[k]}</span>
              {queue[k] && <span className="pl-6 text-[12.5px] text-amber-300">{fmt(t.queued, { date: when(queue[k]!) })}</span>}
            </label>
          ))}
        </div>
      </fieldset>

      {kind === "category_day" && (
        <label className="block max-w-sm">
          <span className="mb-1.5 block text-[13px] text-muted">{t.categoryLabel}</span>
          <select name="category" required defaultValue="" className={`${inputClass} h-11`}>
            <option value="" disabled>
              {t.chooseCategory}
            </option>
            {categories.map((c) => (
              <option key={c.slug} value={c.slug}>
                {c.name}
              </option>
            ))}
          </select>
        </label>
      )}
      {kind === "room_hours" && (
        <label className="block max-w-[200px]">
          <span className="mb-1.5 block text-[13px] text-muted">{t.hoursLabel}</span>
          <input name="hours" type="number" min={1} max={maxHours} value={hours} onChange={(e) => setHours(Math.max(1, Math.min(maxHours, Number(e.target.value) || 1)))} className={`${inputClass} h-11`} />
        </label>
      )}

      {kind !== "telegram" && (
        <fieldset>
          <legend className="mb-3 text-[17px] text-fg">{t.whoTitle}</legend>
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="block">
              <span className="mb-1.5 block text-[13px] text-muted">{t.countryLabel}</span>
              <select name="country" defaultValue="" className={`${inputClass} h-11`}>
                <option value="">{t.anyCountry}</option>
                {countries.map((c) => (
                  <option key={c} value={c}>
                    {regionName(c)}
                  </option>
                ))}
              </select>
            </label>
            {kind !== "room_hours" && (
              <label className="block">
                <span className="mb-1.5 block text-[13px] text-muted">{t.langLabel}</span>
                <select name="lang" defaultValue="" className={`${inputClass} h-11`}>
                  <option value="">{t.anyLanguage}</option>
                  {languages.map((l) => (
                    <option key={l.code} value={l.code}>
                      {l.name}
                    </option>
                  ))}
                </select>
              </label>
            )}
          </div>
        </fieldset>
      )}

      <div className="flex flex-wrap items-center gap-4 rounded-2xl border border-line bg-raised p-4">
        <SweetCoin size={36} />
        <div className="min-w-0 flex-1">
          <p className="text-[17px] text-fg">{fmt(t.total, { n: total.toLocaleString(lang) })}</p>
          <p className="text-[13px] text-muted">
            {fmt(t.balance, { n: balance.toLocaleString(lang) })}
            {balance < total && (
              <>
                {" · "}
                <Link href="/subscription#coins" className="text-brand-soft hover:underline">
                  {t.getCoins}
                </Link>
              </>
            )}
          </p>
        </div>
        <button type="submit" disabled={pending} className="inline-flex h-12 items-center gap-2 rounded-full bg-brand-strong px-6 text-[15px] font-semibold text-white hover:bg-brand-deep disabled:opacity-60">
          <Rocket size={17} aria-hidden />
          {pending ? t.buying : t.buy}
        </button>
      </div>
      <p className="text-[12.5px] text-faint">{t.paidOnly}</p>
      {state.error && (
        <p role="alert" className="rounded-xl border border-brand/30 bg-brand/[0.08] px-4 py-3 text-[14px] text-fg">
          {state.error}
        </p>
      )}
      {state.message && (
        <p role="status" className="rounded-xl border border-emerald-400/25 bg-emerald-500/10 px-4 py-3 text-[14px] text-emerald-200">
          {state.message}
        </p>
      )}
    </form>
  );
}
